import { useMemo, useState, useCallback } from "react";
import { useMapsLibrary, AdvancedMarker } from "@vis.gl/react-google-maps";
import AdjustIcon from "@mui/icons-material/Adjust";
import { Polygon } from "../../../jobsdashboard/map";
import { GeoPreviewMapProps, GeoPreviewMapViewModel, FeaturePopupData } from "./GeoPreviewMap.types";
import { processFeatures, PolygonElementParams, PointElementParams } from "./GeoPreviewMap.helpers";

export function useGeoPreviewMapViewModel(props: GeoPreviewMapProps): GeoPreviewMapViewModel {
	const mapsLibrary = useMapsLibrary("maps");
	const [activePopup, setActivePopup] = useState<FeaturePopupData | null>(null);

	const handleMapClick = useCallback(() => {
		setActivePopup(null);
	}, []);

	// Factory function for creating polygon elements
	const createPolygonElement = useCallback(
		(params: PolygonElementParams) => {
			return (
				<Polygon
					key={params.key}
					paths={params.path}
					fillColor={params.color}
					fillOpacity={0.3}
					strokeColor={params.color}
					strokeWeight={2}
					onClick={(e: google.maps.MapMouseEvent) => {
						if (e.latLng) {
							setActivePopup({
								position: e.latLng.toJSON(),
								properties: params.properties,
							});
						}
					}}
				/>
			);
		},
		[setActivePopup],
	);

	// Factory function for creating point elements
	const createPointElement = useCallback(
		(params: PointElementParams) => {
			return (
				<AdvancedMarker
					key={params.key}
					position={params.position}
					onClick={() => {
						setActivePopup({
							position: params.position,
							properties: params.properties,
						});
					}}
				>
					<AdjustIcon sx={{ color: params.color, fontSize: 16 }} />
				</AdvancedMarker>
			);
		},
		[setActivePopup],
	);

	// Process features using the helper function
	const processedFeatures = useMemo(() => {
		return processFeatures(props.inputData, props.groupIndex, createPolygonElement, createPointElement);
	}, [props.inputData, props.groupIndex, createPolygonElement, createPointElement]);

	return {
		// State
		mapsLibrary,
		activePopup,

		// Computed values
		processedFeatures,

		// Handlers
		setActivePopup,
		handleMapClick,
	};
}
